/**
 * Feld- und regelgenauer Vergleich zwischen dem aktuellen KI-myCheck-Zustand des Editors und einem
 * vom Agenten erzeugten Vorschlagsdokument (`MyCheckProposal.document`).
 * Reine Diff-Logik, kein Backend- oder Bot-Zugriff.
 *
 * Bewusst nur KI in diesem Modul: IDS und Hybrid bekommen eigene Diff-Strategien (Spezifikationsebene
 * bzw. `manifest.steps`/`trigger.config.hybridEditor`), eine gemeinsame `kind`-Verzweigung wäre nur ein
 * Zwischenschritt, den F4 wieder aufbricht.
 */

export interface KiProposalRule {
  id?: string;
  name: string;
  description: string;
  category: string;
  configJson: {
    ruleShortCode?: string;
    allowedDocumentClasses?: string[];
    allowedFileTypes?: string[];
    tagValues?: Record<string, string>;
    parameterValues?: Record<string, string>;
    [key: string]: unknown;
  };
}

export interface KiProposalDocument {
  name: string;
  description: string;
  configJson: Record<string, unknown>;
  rules: KiProposalRule[];
}

/** Art einer Änderung im Vorschlag, bestimmt Badge („Geändert“/„Neu“/„Wird gelöscht“) und Farbe. */
export type ProposalChangeStatus = 'changed' | 'added' | 'removed';

export interface MyCheckFieldChange {
  before: unknown;
  after: unknown;
  /** optional; fehlt er, leitet app-change-marker ihn ab (siehe {@link deriveChangeStatus}) */
  status?: ProposalChangeStatus;
}

/**
 * `before` leer (`''` | `null` | `undefined` | `[]` | `{}`) und `after` nicht leer → `'added'`, sonst
 * `'changed'`. `'removed'` wird nie abgeleitet (ein geleertes Einzelfeld zeigt „Geändert“), nur
 * explizit gesetzt.
 */
export function deriveChangeStatus(change: MyCheckFieldChange): ProposalChangeStatus {
  return isEmptyValue(change.before) && !isEmptyValue(change.after) ? 'added' : 'changed';
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0;
  return false;
}

export interface MyCheckRuleChanges {
  added: KiProposalRule[];
  removed: KiProposalRule[];
  /** Regel-Key (echte `id`) → geänderte Feld-Pfade dieser Regel, z. B. `configJson.parameterValues`. */
  changed: Map<string, string[]>;
}

export interface MyCheckDiff {
  /** Pfad (`name`, `configJson.pruefprompt`, …) → Vorher/Nachher auf Set-Ebene. */
  fieldChanges: Map<string, MyCheckFieldChange>;
  ruleChanges: MyCheckRuleChanges;
}

/**
 * Vergleicht den aktuellen Editor-Zustand (`current`) mit dem Vorschlag (`proposed`) und liefert die
 * Abweichungen auf Set- und Regel-Ebene. Identische Dokumente ergeben einen leeren Diff.
 */
export function computeMyCheckDiff(current: KiProposalDocument, proposed: KiProposalDocument): MyCheckDiff {
  const fieldChanges = new Map<string, MyCheckFieldChange>();
  diffField('name', current.name, proposed.name, fieldChanges);
  diffField('description', current.description, proposed.description, fieldChanges);

  const configKeys = new Set([...Object.keys(current.configJson ?? {}), ...Object.keys(proposed.configJson ?? {})]);
  for (const key of configKeys) {
    diffField(`configJson.${key}`, current.configJson?.[key], proposed.configJson?.[key], fieldChanges);
  }

  return {fieldChanges, ruleChanges: diffRules(current.rules ?? [], proposed.rules ?? [])};
}

function diffField(path: string, before: unknown, after: unknown, into: Map<string, MyCheckFieldChange>): void {
  if (!deepEqual(before, after)) {
    into.set(path, {before, after});
  }
}

/**
 * Regel-Identität ist ausschließlich `id`-basiert (nicht `getRuleKey`, das ist Komponenten-
 * Instanzzustand für lokal erzeugte Schlüssel): eine Regel gilt als "dieselbe" genau dann, wenn beide
 * Seiten eine nicht-leere `id` tragen und diese gleich ist. Regeln ohne `id` auf der Vorschlagsseite
 * sind neu, Regeln ohne Gegenstück im Vorschlag sind entfernt.
 */
function diffRules(current: KiProposalRule[], proposed: KiProposalRule[]): MyCheckRuleChanges {
  const currentById = new Map<string, KiProposalRule>();
  for (const rule of current) {
    const id = (rule.id ?? '').trim();
    if (id) currentById.set(id, rule);
  }

  const matchedCurrentIds = new Set<string>();
  const added: KiProposalRule[] = [];
  const changed = new Map<string, string[]>();

  for (const proposedRule of proposed) {
    const id = (proposedRule.id ?? '').trim();
    if (!id) {
      added.push(proposedRule);
      continue;
    }
    const currentRule = currentById.get(id);
    if (!currentRule) {
      added.push(proposedRule);
      continue;
    }
    matchedCurrentIds.add(id);
    const changedPaths = diffRulePair(currentRule, proposedRule);
    if (changedPaths.length > 0) {
      changed.set(id, changedPaths);
    }
  }

  const removed = current.filter(rule => {
    const id = (rule.id ?? '').trim();
    return !!id && !matchedCurrentIds.has(id);
  });

  return {added, removed, changed};
}

function diffRulePair(current: KiProposalRule, proposed: KiProposalRule): string[] {
  const paths: string[] = [];
  if (!deepEqual(current.name, proposed.name)) paths.push('name');
  if (!deepEqual(current.description, proposed.description)) paths.push('description');
  if (!deepEqual(current.category, proposed.category)) paths.push('category');

  const configKeys = new Set([...Object.keys(current.configJson ?? {}), ...Object.keys(proposed.configJson ?? {})]);
  for (const key of configKeys) {
    if (!deepEqual(current.configJson?.[key], proposed.configJson?.[key])) {
      paths.push(`configJson.${key}`);
    }
  }
  return paths;
}

/**
 * Kleine handgeschriebene Struktur-Gleichheit: Werte sind hier ausschließlich JSON-sichere Strings,
 * Zahlen, Booleans, Arrays und flache `Record<string, string>`-Maps – kein Bedarf für eine Bibliothek.
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return a === b;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((value, index) => deepEqual(value, b[index]));
  }

  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
}

/** Formatiert `before`/`after`-Werte für die Tooltip-Anzeige im `change-marker`. */
export function defaultFormatChangeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '–';
  if (typeof value === 'string') return value;
  if (Array.isArray(value) || typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Parst einen als JSON-String transportierten `configJson`-Wert; Objekte gehen unverändert durch. */
function parseConfigJson(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

/**
 * Normalisiert ein KI-Vorschlagsdokument auf das erwartete Schema (`configJson` je Set und je Regel als
 * **Objekt**). Der Bot liefert Regel-`configJson` teils als JSON-String (Server-DTO-Form); ohne diese
 * Normalisierung vergleicht `computeMyCheckDiff` Zeichenpositionen des Strings und `toKiRule` liest
 * `tagValues`/`parameterValues` als leer – siehe docs/mycheck-proposal-backend-plan.md.
 */
export function normalizeKiProposalDocument(doc: KiProposalDocument): KiProposalDocument {
  return {
    ...doc,
    configJson: parseConfigJson(doc.configJson),
    rules: (doc.rules ?? []).map(rule => ({...rule, configJson: parseConfigJson(rule.configJson)})),
  };
}

/** Zeilenweiser Unterschied einer Parameterliste (z. B. Ausgabeparameter), Zeilen als Objekt-Referenzen. */
export interface RowDiff<T> {
  /** Zeilen nur im Vorschlag (Referenzen auf Zeilen aus `after`). */
  added: T[];
  /** Zeile aus `after` → ihr Vorher-Stand, wenn sich mindestens ein Feld unterscheidet. */
  changed: Map<T, T>;
  /** Zeilen, die der Vorschlag entfernt, mit ihrer alten Position. */
  removed: { before: T; index: number }[];
}

/**
 * Vergleicht zwei Parameterlisten zeilenweise. Zeilen werden über ihren Namen zugeordnet; bleiben
 * danach eine neue und eine entfernte Zeile an derselben Position übrig, gilt das als Umbenennung
 * (geänderte Zeile) statt als entfernt + neu.
 */
export function diffRows<T extends object>(before: T[], after: T[]): RowDiff<T> {
  const keyOf = (row: T): string => ((row as { name?: string }).name ?? '').trim().toLowerCase();
  const comparable = (row: T): unknown => ({...row, options: (row as { options?: unknown }).options ?? []});

  const unmatchedBefore = new Map<string, number[]>();
  before.forEach((row, index) => {
    const key = keyOf(row);
    unmatchedBefore.set(key, [...(unmatchedBefore.get(key) ?? []), index]);
  });

  const changed = new Map<T, T>();
  const unmatchedAfter: number[] = [];
  after.forEach((row, index) => {
    const beforeIndex = unmatchedBefore.get(keyOf(row))?.shift();
    if (beforeIndex === undefined) {
      unmatchedAfter.push(index);
      return;
    }
    if (!deepEqual(comparable(before[beforeIndex]), comparable(row))) changed.set(row, before[beforeIndex]);
  });

  const leftoverBefore = new Set([...unmatchedBefore.values()].flat());
  const added: T[] = [];
  for (const index of unmatchedAfter) {
    if (leftoverBefore.has(index)) {
      changed.set(after[index], before[index]);
      leftoverBefore.delete(index);
    } else {
      added.push(after[index]);
    }
  }
  const removed = [...leftoverBefore].sort((a, b) => a - b).map(index => ({before: before[index], index}));
  return {added, changed, removed};
}
